import { useTranslation } from 'react-i18next';
import { Icon, Paperclip, Plus, Sparkles, X } from '../../ui/Icon';
import ConfirmModal from '../../ui/ConfirmModal';
import { ATTACHMENT_ACCEPT, fileIcon, fileUrl, formatBytes, stagedFileIcon, FIELD_LABEL, type StoredDocument } from './documentFiles';

interface Props {
  /** Already-uploaded documents; omit on forms that only stage files (new records). */
  existing?: StoredDocument[];
  /** Required whenever `existing` is passed. */
  onDeleteExisting?: (doc: StoredDocument) => Promise<void> | void;
  docToDelete?: StoredDocument | null;
  onRequestDelete?: (doc: StoredDocument | null) => void;

  staged: File[];
  onAddFiles: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveStaged: (index: number) => void;
  fileInputKey: number;

  onExtract: () => void;
  extracting: boolean;
}

/**
 * The attachments block of the booking forms: existing documents, files staged
 * for upload, and the "fill in with AI" trigger. One component rather than the
 * three near-identical copies the flight, hotel and booking forms each carried.
 */
export function DocumentAttachments({
  existing = [], onDeleteExisting, docToDelete, onRequestDelete,
  staged, onAddFiles, onRemoveStaged, fileInputKey, onExtract, extracting,
}: Props) {
  const { t } = useTranslation();
  const isEmpty = staged.length === 0 && existing.length === 0;

  const filePicker = (
    <input key={fileInputKey} type="file" multiple accept={ATTACHMENT_ACCEPT} className="hidden" onChange={onAddFiles} />
  );

  return (
    <div>
      <label className={FIELD_LABEL}>{t('bookings.form.documents')}</label>

      {existing.length > 0 && (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden mb-2">
          {existing.map(doc => (
            <div key={doc.id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-700 last:border-0 text-xs">
              <Icon as={fileIcon(doc.mimeType)} size={14} className="text-slate-400 dark:text-slate-500 shrink-0" />
              <a
                href={fileUrl(doc.filename)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 truncate text-teal-600 hover:underline"
              >
                {doc.originalName}
              </a>
              <span className="text-slate-400 dark:text-slate-500 shrink-0">{formatBytes(doc.size)}</span>
              <button
                type="button"
                onClick={() => onRequestDelete?.(doc)}
                className="text-slate-300 dark:text-slate-600 hover:text-red-400 cursor-pointer"
                aria-label={t('common.delete')}
              >
                <Icon as={X} size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {onDeleteExisting && (
        <ConfirmModal
          open={!!docToDelete}
          title={t('bookings.docs.deleteTitle')}
          body={docToDelete ? t('bookings.docs.deleteConfirm', { name: docToDelete.originalName }) : undefined}
          onConfirm={() => docToDelete && onDeleteExisting(docToDelete)}
          onCancel={() => onRequestDelete?.(null)}
        />
      )}

      {isEmpty ? (
        <label className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-slate-200 dark:border-slate-700 text-xs text-slate-400 dark:text-slate-500 hover:border-teal-300 dark:hover:border-teal-600 hover:text-teal-500 hover:bg-teal-50/50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer">
          {filePicker}
          <Icon as={Paperclip} size={14} />
          {t('bookings.docs.attachFile')}
        </label>
      ) : (
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
          {staged.map((f, i) => (
            <div key={i} className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 dark:border-slate-700 last:border-0 text-xs">
              <Icon
                as={stagedFileIcon(f)}
                size={14}
                className="text-slate-400 dark:text-slate-500 shrink-0"
              />
              <span className="flex-1 min-w-0 truncate text-slate-700 dark:text-slate-300">{f.name}</span>
              <span className="text-slate-400 dark:text-slate-500 shrink-0">{formatBytes(f.size)}</span>
              <button
                type="button"
                onClick={() => onRemoveStaged(i)}
                className="text-slate-300 dark:text-slate-600 hover:text-red-400 cursor-pointer"
                aria-label={t('common.delete')}
              >
                <Icon as={X} size={14} />
              </button>
            </div>
          ))}
          <label className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-slate-400 dark:text-slate-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors cursor-pointer">
            {filePicker}
            <Icon as={Plus} size={12} />
            {t('bookings.docs.addMore')}
          </label>
        </div>
      )}

      {staged.length > 0 && (
        <button
          type="button"
          onClick={onExtract}
          disabled={extracting}
          className="mt-2 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 text-xs font-semibold text-teal-700 dark:text-teal-300 hover:bg-teal-100 dark:hover:bg-teal-900/40 transition-colors cursor-pointer disabled:opacity-50 disabled:pointer-events-none"
        >
          <Icon as={Sparkles} size={14} />
          {t('bookings.form.extractWithAi')}
        </button>
      )}
    </div>
  );
}
